import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * VEILX — Sticky header menu stability fix
 *
 * Why this exists:
 * The theme's normal `scroll-lock` system changes the overflow state of the
 * page while the header is sticky. After the page has been scrolled, that can
 * move the sticky header/drawer outside the visible viewport while its
 * backdrop still blocks the page.
 *
 * This version keeps the current page scroll container unchanged. While the
 * main menu is open it:
 * - pins the header section to the viewport;
 * - freezes the existing page scroll position without changing overflow;
 * - allows the menu drawer to scroll normally;
 * - restores the original header and page state after closing.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The main details element.
 * @property {HTMLDivElement} menuDrawer - The visible menu drawer.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Element | null} */
  #pageScrollContainer = null;

  /** @type {number} */
  #lockedScrollTop = 0;

  /** @type {HTMLElement | null} */
  #headerSection = null;

  /** @type {HTMLElement | null} */
  #headerComponent = null;

  /** @type {boolean} */
  #pageLocked = false;

  connectedCallback() {
    super.connectedCallback();

    /*
     * Disable the theme-wide overflow lock for this drawer only.
     * Search, cart and other dialogs keep their original locking behaviour.
     */
    this.refs.details.removeAttribute('scroll-lock');

    this.#injectStabilityStyles();
    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.details.addEventListener('toggle', this.#onNativeToggle);
    window.addEventListener('pagehide', this.#forceCleanup);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#onNativeToggle);
    window.removeEventListener('pagehide', this.#forceCleanup);
    this.#unlockPage();
  }

  /**
   * Close the main menu drawer when Escape is pressed.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Safety cleanup if the native details state is cleared unexpectedly.
   */
  #onNativeToggle = () => {
    if (!this.refs.details.open) {
      this.#unlockPage();
      removeTrapFocus();
    }
  };

  #forceCleanup = () => {
    this.#unlockPage();
  };

  /**
   * Keep the underlying page at exactly the same scroll position.
   */
  #holdPagePosition = () => {
    const container = this.#pageScrollContainer;
    if (!container) return;

    if (container.scrollTop !== this.#lockedScrollTop) {
      container.scrollTop = this.#lockedScrollTop;
    }
  };

  /**
   * Prevent wheel/touch scrolling on the backdrop while preserving normal
   * scrolling inside the menu drawer.
   * @param {WheelEvent | TouchEvent} event
   */
  #preventBackgroundScroll = (event) => {
    if (event.composedPath().includes(this.refs.menuDrawer)) return;
    event.preventDefault();
  };

  /**
   * Prevent keyboard scrolling outside the drawer.
   * @param {KeyboardEvent} event
   */
  #preventBackgroundKeyScroll = (event) => {
    const scrollKeys = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);
    if (!scrollKeys.has(event.key)) return;
    if (event.composedPath().includes(this.refs.menuDrawer)) return;

    const target = event.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    ) {
      return;
    }

    event.preventDefault();
  };

  /**
   * Get the active page scroll container used by this theme.
   * @returns {Element}
   */
  #getPageScrollContainer() {
    if (window.matchMedia('(min-width: 990px)').matches) {
      return document.querySelector('.page-wrapper') ?? document.scrollingElement ?? document.documentElement;
    }

    return document.scrollingElement ?? document.documentElement;
  }

  /**
   * Freeze the page without applying overflow:hidden to the sticky header's
   * scroll container.
   */
  #lockPage() {
    if (this.#pageLocked) return;

    this.#pageScrollContainer = this.#getPageScrollContainer();
    this.#lockedScrollTop = this.#pageScrollContainer.scrollTop;
    this.#headerSection = this.closest('.header-section');
    this.#headerComponent = this.closest('header-component');

    document.documentElement.classList.add('veilx-header-drawer-open');
    this.#headerSection?.classList.add('veilx-header-section--drawer-open');
    this.#headerComponent?.classList.add('veilx-header-component--drawer-open');

    this.#pageScrollContainer.addEventListener('scroll', this.#holdPagePosition, { passive: true });
    document.addEventListener('wheel', this.#preventBackgroundScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', this.#preventBackgroundScroll, { passive: false, capture: true });
    document.addEventListener('keydown', this.#preventBackgroundKeyScroll, { capture: true });

    this.#pageLocked = true;
  }

  /**
   * Restore the normal sticky header and page scrolling.
   */
  #unlockPage() {
    if (!this.#pageLocked) return;

    this.#pageScrollContainer?.removeEventListener('scroll', this.#holdPagePosition);
    document.removeEventListener('wheel', this.#preventBackgroundScroll, { capture: true });
    document.removeEventListener('touchmove', this.#preventBackgroundScroll, { capture: true });
    document.removeEventListener('keydown', this.#preventBackgroundKeyScroll, { capture: true });

    document.documentElement.classList.remove('veilx-header-drawer-open');
    this.#headerSection?.classList.remove('veilx-header-section--drawer-open');
    this.#headerComponent?.classList.remove('veilx-header-component--drawer-open');

    if (this.#pageScrollContainer) {
      this.#pageScrollContainer.scrollTop = this.#lockedScrollTop;
    }

    this.#pageScrollContainer = null;
    this.#headerSection = null;
    this.#headerComponent = null;
    this.#pageLocked = false;
  }

  /**
   * Add the small viewport-stability layer required by the fix.
   */
  #injectStabilityStyles() {
    if (document.getElementById('veilx-header-drawer-stability-styles')) return;

    const style = document.createElement('style');
    style.id = 'veilx-header-drawer-stability-styles';
    style.textContent = `
      html.veilx-header-drawer-open,
      html.veilx-header-drawer-open body,
      html.veilx-header-drawer-open .page-wrapper {
        overscroll-behavior: none;
      }

      .veilx-header-section--drawer-open {
        position: fixed !important;
        inset: 0 0 auto 0 !important;
        width: 100% !important;
        z-index: calc(var(--layer-menu-drawer, 18) + 10) !important;
      }

      .veilx-header-component--drawer-open {
        opacity: 1 !important;
        visibility: visible !important;
        transform: none !important;
        contain: style !important;
      }

      html.veilx-header-drawer-open .menu-drawer {
        position: fixed !important;
        top: 0 !important;
        bottom: 0 !important;
        height: 100dvh !important;
        max-height: 100dvh !important;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
      }

      html.veilx-header-drawer-open .menu-drawer__backdrop {
        position: fixed !important;
        inset: 0 !important;
        width: 100vw !important;
        height: 100dvh !important;
        pointer-events: auto !important;
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open.
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target.
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer.
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      /* Only the main drawer owns the page lock. */
      if (details === this.refs.details) {
        this.#lockPage();
      }

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer.
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer.
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest open menu or submenu.
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#unlockPage();
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Remove will-change after menu item animations.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disable accordion animations during initial drawer opening.
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state.
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}